import React, { useContext, useEffect, useRef, useState } from "react"
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react"
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    HandbagIcon,
    StarIcon,
    XIcon,
} from "@phosphor-icons/react"
import QuickBuyContext from "@context/QuickBuyContext"
import AddToWishListButton from "@components/common/AddToWishListButton"
import SwipeHint from "@images/icons/swipe-hint.gif"

const SWIPE_THRESHOLD = 45
const HINT_DURATION = 4000

const MONTHS = [
    "Jan.", "Feb.", "Mar.", "Apr.", "May", "Jun.",
    "Jul.", "Aug.", "Sep.", "Oct.", "Nov.", "Dec.",
]

const formatReviewDate = (value) => {
    if (!value) return ""

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) return ""

    return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
}

const MediaView = ({ media }) => {
    const videoRef = useRef(null)
    const [muted, setMuted] = useState(true)

    useEffect(() => {
        setMuted(true)
    }, [media?.fullSrc])

    useEffect(() => {
        if (videoRef.current) videoRef.current.muted = muted
    }, [muted, media?.fullSrc])

    if (!media) return null

    const alt = media.review?.title || media.review?.productTitle || "Review image"

    if (media.type === "video") {
        const poster = media.thumbSrc && media.thumbSrc !== media.fullSrc ? media.thumbSrc : undefined

        return (
            <video
                key={media.fullSrc}
                ref={videoRef}
                src={media.fullSrc}
                poster={poster}
                autoPlay
                loop
                muted={muted}
                playsInline
                controls={!muted}
                onClick={() => setMuted(false)}
                className="w-full h-full object-contain cursor-pointer"
            />
        )
    }

    return (
        <img
            key={media.fullSrc}
            src={media.fullSrc}
            alt={alt}
            className="w-full h-full object-contain"
        />
    )
}

const SlidePreview = ({ media }) => {
    if (!media) return null

    return (
        <img
            src={media.thumbSrc || media.fullSrc}
            alt=""
            className="w-full h-full object-contain"
        />
    )
}

const ReviewImagePopup = ({ isOpen = false, onClose = () => {}, images = [], startIndex = 0, product, renderProductCard }) => {
    const [currentIndex, setCurrentIndex] = useState(startIndex)
    const [showHint, setShowHint] = useState(false)
    const [dragX, setDragX] = useState(0)
    const [animate, setAnimate] = useState(false)
    const touchStart = useRef(null)
    const wrapperRef = useRef(null)
    const widthRef = useRef(0)
    const movedRef = useRef(false)
    const axisRef = useRef(null)
    const pendingDirRef = useRef(null)
    const { setAddToBagClicked } = useContext(QuickBuyContext)

    const total = images.length
    const current = images[currentIndex]
    const review = current?.review
    const showArrows = total > 1
    const prevIndex = total > 0 ? (currentIndex - 1 + total) % total : 0
    const nextIndex = total > 0 ? (currentIndex + 1) % total : 0
    const dragOffset = `${dragX >= 0 ? "+" : "-"} ${Math.abs(dragX)}px`
    const trackStyle = {
        transform: `translate3d(calc(-100% ${dragOffset}), 0, 0)`,
        transition: animate ? "transform 300ms ease-out" : "none",
    }

    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(startIndex)
        }
    }, [isOpen, startIndex])

    useEffect(() => {
        if (!isOpen || total <= 1) {
            setShowHint(false)
            return undefined
        }

        setShowHint(true)
        const timer = setTimeout(() => setShowHint(false), HINT_DURATION)
        return () => clearTimeout(timer)
    }, [isOpen, total, startIndex])

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev - 1 + total) % total)
    }

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % total)
    }

    const handleTouchStart = (e) => {
        if (total <= 1) return

        if (pendingDirRef.current === "next") handleNext()
        else if (pendingDirRef.current === "prev") handlePrev()
        pendingDirRef.current = null

        const point = e.touches[0]
        touchStart.current = { x: point.clientX, y: point.clientY }
        widthRef.current = wrapperRef.current?.offsetWidth || 0
        movedRef.current = false
        axisRef.current = null
        setAnimate(false)
        setDragX(0)
        setShowHint(false)
    }

    const handleTouchMove = (e) => {
        if (!touchStart.current || total <= 1) return

        const point = e.touches[0]
        const deltaX = point.clientX - touchStart.current.x
        const deltaY = point.clientY - touchStart.current.y

        if (!axisRef.current) {
            if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return
            axisRef.current = Math.abs(deltaX) > Math.abs(deltaY) ? "x" : "y"
        }

        if (axisRef.current !== "x") return

        movedRef.current = true
        setDragX(deltaX)
    }

    const handleTouchEnd = (e) => {
        if (!touchStart.current || total <= 1) {
            touchStart.current = null
            return
        }

        const point = e.changedTouches[0]
        const deltaX = point.clientX - touchStart.current.x
        const deltaY = point.clientY - touchStart.current.y
        const moved = movedRef.current
        touchStart.current = null

        if (!moved) return

        const horizontal = Math.abs(deltaX) > Math.abs(deltaY)

        if (horizontal && Math.abs(deltaX) >= SWIPE_THRESHOLD) {
            const width = widthRef.current || 0
            pendingDirRef.current = deltaX < 0 ? "next" : "prev"
            setAnimate(true)
            setDragX(deltaX < 0 ? -width : width)
        } else {
            pendingDirRef.current = null
            setAnimate(true)
            setDragX(0)
        }
    }

    const handleTransitionEnd = (e) => {
        if (e.target !== e.currentTarget) return

        const dir = pendingDirRef.current
        pendingDirRef.current = null

        if (dir === "next") handleNext()
        else if (dir === "prev") handlePrev()

        setAnimate(false)
        setDragX(0)
    }

    const handleAddToBag = () => {
        if (!product) return
        setAddToBagClicked(product)
        onClose()
    }

    const metaLine = formatReviewDate(review?.dateCreated)

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-[100]">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-black/80 transition-opacity duration-300 ease-out data-[closed]:opacity-0"
            />

            <div className="fixed inset-0 w-screen overflow-y-auto">
                <div className="flex min-h-full items-stretch lg:items-center justify-center p-0 lg:p-4">
                    <DialogPanel
                        transition
                        className="relative w-full h-[100dvh] max-w-none rounded-none bg-[#0a0a0a] lg:h-[559px] lg:max-h-[90vh] lg:max-w-[1000px] lg:rounded-[2px] lg:bg-white transform transition-all duration-300 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
                    >
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="hidden lg:block absolute top-0 right-0 z-20 p-[18px] drop-shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.06)]"
                        >
                            <XIcon size={24} weight="light" className="text-primary" />
                        </button>

                        {/* Desktop layout */}
                        <div className="hidden lg:flex flex-row h-full w-full">
                            <div className="flex-1 min-h-0 bg-black rounded-[2px] flex items-center justify-center overflow-hidden">
                                <MediaView media={current} />
                            </div>

                            <div className="flex-1 min-h-0 flex flex-col items-end px-4 py-8">
                                <div className="flex-1 min-h-0 w-full flex flex-col gap-4 py-10">
                                    <div className="flex-1 min-h-0 w-full flex flex-col gap-4 overflow-y-auto">
                                        <div className="flex gap-[14px] items-start w-full">
                                            <p className="flex-1 min-w-0 m-0 text-[14px] leading-[20px] font-medium text-[#3d3d3d] break-words">
                                                {review?.author}{review?.region ? `, ${review.region}` : ""}
                                            </p>
                                            <div className="shrink-0 flex items-center gap-1">
                                                <StarIcon size={14} weight="fill" className="text-primary" />
                                                <span className="text-[12px] leading-[16px] text-[#3d3d3d]">
                                                    {review?.rating}
                                                </span>
                                            </div>
                                        </div>

                                        {review?.title && (
                                            <p className="m-0 text-[14px] leading-[20px] font-medium text-[#3d3d3d] break-words">
                                                {review?.title}
                                            </p>
                                        )}

                                        <p className="m-0 text-[14px] leading-[20px] text-[#3d3d3d] break-words">
                                            {review?.body}
                                        </p>
                                    </div>

                                    <div className="flex gap-[14px] items-start w-full">
                                        <p className="flex-1 min-w-0 m-0 text-[12px] leading-[16px] text-[#3d3d3d]">
                                            {metaLine}
                                        </p>
                                    </div>

                                    {renderProductCard && current && (
                                        <div className="w-full shrink-0 flex flex-col gap-4">
                                            <div className="h-px w-full bg-[#e7e7e7]" />
                                            {renderProductCard(current)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Mobile layout */}
                        <div className="flex lg:hidden flex-col h-full w-full bg-[#0a0a0a]">
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Back"
                                className="shrink-0 flex items-center gap-3 p-4 z-20"
                            >
                                <ArrowLeftIcon size={20} weight="regular" className="text-white" />
                                <span className="text-[14px] leading-[20px] text-white">BACK</span>
                            </button>

                            <div
                                ref={wrapperRef}
                                className="relative flex-1 min-h-0 bg-black overflow-hidden touch-pan-y"
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                            >
                                {total > 1 ? (
                                    <div className="flex h-full w-full" style={trackStyle} onTransitionEnd={handleTransitionEnd}>
                                        <div className="shrink-0 w-full h-full flex items-center justify-center">
                                            <SlidePreview media={images[prevIndex]} />
                                        </div>
                                        <div className="shrink-0 w-full h-full flex items-center justify-center">
                                            <MediaView media={current} />
                                        </div>
                                        <div className="shrink-0 w-full h-full flex items-center justify-center">
                                            <SlidePreview media={images[nextIndex]} />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="h-full w-full flex items-center justify-center">
                                        <MediaView media={current} />
                                    </div>
                                )}
                                {total > 1 && (
                                    <div
                                        className={`pointer-events-none absolute inset-x-0 bottom-6 flex items-end justify-center transition-opacity duration-500 ${
                                            showHint ? "opacity-100" : "opacity-0"
                                        }`}
                                    >
                                        <img src={SwipeHint} alt="" className="w-[71px] h-[71px]" />
                                    </div>
                                )}
                            </div>

                            <div className="shrink-0 px-4 pt-4">
                                <div className="relative h-[120px]">
                                    <div className="h-full overflow-y-auto scrollbar-hide flex flex-col gap-2 pb-6">
                                        <div className="flex gap-[14px] items-start w-full">
                                            <p className="flex-1 min-w-0 m-0 text-[14px] leading-[20px] font-medium text-white break-words">
                                                {review?.author}{review?.region ? `, ${review.region}` : ""}
                                            </p>
                                            <div className="shrink-0 flex items-center gap-1">
                                                <StarIcon size={14} weight="fill" className="text-white" />
                                                <span className="text-[12px] leading-[16px] text-white">
                                                    {review?.rating}
                                                </span>
                                            </div>
                                        </div>

                                        {review?.title && (
                                            <p className="m-0 text-[14px] leading-[20px] font-medium text-white break-words">
                                                {review?.title}
                                            </p>
                                        )}

                                        <p className="m-0 text-[14px] leading-[20px] text-white break-words">
                                            {review?.body}
                                        </p>
                                    </div>
                                    <div
                                        className="pointer-events-none absolute inset-x-0 bottom-0 h-10"
                                        style={{ backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0) 0%, #0a0a0a 90%)" }}
                                    />
                                </div>
                            </div>

                            <div className="shrink-0 px-4 pb-2">
                                <p className="m-0 text-[12px] leading-[16px] text-white">{metaLine}</p>
                            </div>

                            {renderProductCard && current && (
                                <div className="shrink-0 bg-white p-4 drop-shadow-[0px_-2px_12px_rgba(0,0,0,0.1)]">
                                    {renderProductCard(current, { mobile: true })}
                                </div>
                            )}

                            {product && (
                                <div className="shrink-0 bg-white flex items-center gap-2 p-4 drop-shadow-[0px_-2px_12px_rgba(0,0,0,0.1)]">
                                    <button
                                        type="button"
                                        onClick={handleAddToBag}
                                        className="flex-1 bg-black text-white flex items-center justify-center gap-3 px-4 py-3"
                                    >
                                        <HandbagIcon size={20} weight="light" />
                                        <span className="text-[16px] leading-[24px]">Add to Bag</span>
                                    </button>
                                    <div className="flex items-center justify-center w-12 self-stretch border border-[#d1d1d1] bg-white">
                                        <AddToWishListButton
                                            data={product}
                                            noRoundedBg
                                            disableTooltip
                                            heartSize={20}
                                            className="flex items-center justify-center w-full h-full text-black"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {showArrows && (
                            <React.Fragment>
                                <button
                                    type="button"
                                    onClick={handlePrev}
                                    aria-label="Previous review image"
                                    className="hidden lg:flex absolute top-1/2 -translate-y-1/2 left-[-30px] z-20 items-center justify-center p-[18px] rounded-full bg-[#0a0a0a]"
                                >
                                    <ArrowLeftIcon size={24} weight="regular" className="text-white" />
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    aria-label="Next review image"
                                    className="hidden lg:flex absolute top-1/2 -translate-y-1/2 right-[-30px] z-20 items-center justify-center p-[18px] rounded-full bg-[#0a0a0a]"
                                >
                                    <ArrowRightIcon size={24} weight="regular" className="text-white" />
                                </button>
                            </React.Fragment>
                        )}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
}

export default ReviewImagePopup
